import type GameLocal from "~/libs/interfaces/GameLocal";
import type GameFilters from "~/libs/interfaces/GameFilters";
import NotFoundError from "~/libs/errors/NotFoundError";
import type {BaseApiFetchOptions} from "~/libs/interfaces/BaseApiFetchOptions";
import type GameStrapi from "~/libs/interfaces/GameStrapi";
import gameLocalFactory from "~/libs/factories/GameLocalFactory";
import type StatisticsLocal from "~/libs/interfaces/StatisticsLocal";
import statisticsLocalFactory from "~/libs/factories/StatisticsLocalFactory";

const gameStore = (type: string = 'game', key?: string) => defineStore(`games-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      games: GameLocal[]
      game: Ref<GameLocal | null>
      stats: Ref<StatisticsLocal | null>
      filters: GameFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      games: reactive([]),
      game: ref(null),
      stats: ref(null),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get games with applied filters and presets
     * @param offset
     * @param limit
     * @return GameLocal[]
     */
    getFilteredGames(offset: number = 0, limit: number = 10): GameLocal[] {
      let matchedCount = 0

      const games = this.games.map((game) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...game,
          isEnabled: matchFilters
        }
      })
      return games
    },

    /**
     * Get only filtered games (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredGames(offset: number = 0, limit: number = 10): GameLocal[] {
      const games = this.getFilteredGames(offset, limit)
      return games.filter((game) => game.isEnabled)
    },

    /**
     * Get count of getFilteredGames
     * @return number
     */
    getFilteredGamesCount(): number {
      const games = this.getFilteredGames(0, this.games.length).filter(game => game.isEnabled)
      return games?.length || 0
    },

    /**
     * Get count of items
     * @return number
     */
    getGamesCount(): number {
      return this.games.length || 0
    },
    /**
     * Get game by id
     * @param id
     */
    getGame(id: string|number): GameLocal | undefined {
      const game = this.games.find((game) => {
        return String(game.id) === String(id)
      })
      return game
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: GameFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      // check item slugs filter
      if (!sameArrays(filters?.slugs, this.filters.slugs)) {
        return true
      }

      return false
    },

    checkFiltersChanges(filters: GameFilters): void {
      // reset items if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.filters.slugs = filters.slugs
        this.games = reactive([])
        this.game = null
      }
    },

    /**
     * Fetch items from server
     * @return {Promise<void>}
     */
    async fetchGames(
      filters: GameFilters = {},
      options: BaseApiFetchOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const { find } = useStrapi()

      this.isLoading = true

      const apiFilters = {
        ...(filters.ids && {
          documentId: {
            $in: filters.ids,
          }
        }),
        ...(filters.slugs && {
          slug: {
            $in: filters.slugs,
          }
        })
      }

      const params = {
        filters: apiFilters,
        status: "published",
      }

      try {
        const result = await find<GameStrapi>('games', params)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useGamesStore:fetchGames - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },

    async fetchStatistics(): Promise<StatisticsLocal> {
      const logger = useFrontendLogger()
      const { find } = useStrapi()

      try {
        const result = await find<any>('statistics')
        if (result) {
          this.stats = statisticsLocalFactory.createStatisticsFromStrapi(result) || null
        }
        return this.stats
      } catch (e) {
        logger?.error('useGameStore:fetchStatistics - exception', {
          errorDetail: e,
        })
        this.isLoading = false
        throw e
      }
    },

    /**
     * Process loaded data from Strapi
     * @param newItems
     * @param isDetail
     */
    processData(newItems: GameStrapi[], isDetail: boolean = false):void {
      if (newItems.length) {
        // check item detail load
        if (isDetail && newItems[0]) {
          this.setItem(gameLocalFactory.createGameFromStrapi(newItems[0]))
        } else if (isDetail && (!newItems || !newItems.length)) {
          throw new NotFoundError('Game not found')
        }
        if (newItems && newItems.length) {
          // add new items to list
          this.addItems(newItems.map(gameLocalFactory.createGameFromStrapi).filter(item => !!item), true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Game not found')
      }
    },
    /**
     * Add items to store - remove duplicities
     * @param newItems
     * @param rewrite
     */
    addItems(newItems?: GameLocal[], rewrite: boolean = false): void {
      newItems?.forEach((newItem) => {
        const foundIndex = this.games.findIndex(game => game.id === newItem.id)
        if (foundIndex >= 0 && rewrite) {
          this.games.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.games.push(newItem)
        }
      })
    },

    setItem(item?: GameLocal): void {
      if (item) {
        this.game = item
      } else {
        this.game = null
      }
    }

  }
})

export const useGamesStore = (type?: string, key?: string) => {
  return gameStore(type, key)()
}
